import { registerAndWait } from '../serviceWorker';
import { confirmDialog } from '../components/helpers/dialogUtils';
import II18 from '../components/translate/II18';
import { f7 } from 'framework7-react';

const STARTUP_UPDATE_PRELOADER_ID = 'pwa-startup-update-preloader';
const STARTUP_UPDATE_TIMEOUT_MS = 15000;

let runningUpdatePromptShown = false;

const applyUpdate = registration => {
  if (registration?.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }
};

const showStartupUpdatePreloader = () => {
  if (document.getElementById(STARTUP_UPDATE_PRELOADER_ID)) {
    return;
  }

  const overlay = document.createElement('div');
  overlay.id = STARTUP_UPDATE_PRELOADER_ID;
  overlay.innerHTML = `
    <style>
      #${STARTUP_UPDATE_PRELOADER_ID} {
        position: fixed;
        inset: 0;
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
        color: #333;
        font-family: system-ui, -apple-system, sans-serif;
      }
      #${STARTUP_UPDATE_PRELOADER_ID} .pwa-startup-update-preloader__inner {
        text-align: center;
        padding: 24px;
      }
      #${STARTUP_UPDATE_PRELOADER_ID} .pwa-startup-update-preloader__spinner {
        width: 40px;
        height: 40px;
        margin: 0 auto 16px;
        border: 3px solid rgba(33, 150, 243, 0.2);
        border-top-color: #2196f3;
        border-radius: 50%;
        animation: pwa-startup-update-spin 0.8s linear infinite;
      }
      @keyframes pwa-startup-update-spin {
        to { transform: rotate(360deg); }
      }
    </style>
    <div class="pwa-startup-update-preloader__inner">
      <div class="pwa-startup-update-preloader__spinner"></div>
      <p>${II18.get('pwa_update_applying')}</p>
    </div>
  `;

  document.body.appendChild(overlay);
};

const hideStartupUpdatePreloader = () => {
  document.getElementById(STARTUP_UPDATE_PRELOADER_ID)?.remove();
};

/** At cold start: apply waiting SW immediately and show HTML preloader until reload. */
const forceUpdateAtStartup = registration =>
  new Promise(resolve => {
    if (!registration?.waiting) {
      resolve();
      return;
    }

    showStartupUpdatePreloader();
    applyUpdate(registration);

    // controllerchange → reload is expected; fallback so bootstrap cannot hang forever.
    setTimeout(() => {
      hideStartupUpdatePreloader();
      resolve();
    }, STARTUP_UPDATE_TIMEOUT_MS);
  });

const promptWhileRunning = registration => {
  if (runningUpdatePromptShown || !registration?.waiting) {
    return Promise.resolve();
  }
  runningUpdatePromptShown = true;

  return new Promise(resolve => {
    const showDialog = () => {
      if (!registration.waiting) {
        runningUpdatePromptShown = false;
        resolve();
        return;
      }
      if (!f7?.dialog) {
        setTimeout(showDialog, 400);
        return;
      }

      confirmDialog(
        II18.get('pwa_update_title'),
        II18.get('pwa_update_message'),
        () => {
          applyUpdate(registration);
          resolve();
        },
        () => resolve(),
        II18.get('pwa_update_now'),
        II18.get('pwa_update_later')
      );
    };

    showDialog();
  });
};

const promptForUpdate = (registration, { atStartup } = {}) =>
  atStartup
    ? forceUpdateAtStartup(registration)
    : promptWhileRunning(registration);

/**
 * Raw signal: this document is controlled by a service worker (shell/assets
 * may come from SW precache, not a plain network navigation).
 */
export function isPageControlledByServiceWorker() {
  return Boolean(
    typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator &&
      navigator.serviceWorker.controller
  );
}

/**
 * Run SW register/update bootstrap when:
 * - standalone → allow first install + update checks, or
 * - page already controlled by SW → update checks even if window.standalone is false
 *   (e.g. home-screen session that cleared the product flag but still has SW).
 */
export function shouldBootstrapServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }
  return Boolean(window.standalone) || isPageControlledByServiceWorker();
}

/** Register (if needed) and wait for the initial update check before app start. */
export function bootstrapServiceWorker() {
  return registerAndWait({ onUpdate: promptForUpdate });
}

/** @deprecated Use bootstrapServiceWorker — same behavior. */
export function bootstrapStandalone() {
  return bootstrapServiceWorker();
}
